import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { forkJoin, Subscription } from 'rxjs';

import { SdsApiService } from '../services/sds-api.service';

/**
 * A PDF chosen from the business Archive. `id` is the archive document id, which the
 * backend re-resolves server-side.
 */
export interface SdsArchiveFile {
  id: number;
  name: string;
  size: number | null;
}

/**
 * Reusable modal that lists the PDF documents in the business Archive's Chemicals folder
 * (via a lightweight, name-sorted endpoint) and emits the one the user picks. The user can
 * preview a file before confirming.
 */
@Component({
  selector: 'app-sds-archive-picker',
  templateUrl: './sds-archive-picker.component.html',
  styleUrls: ['./sds-archive-picker.component.css'],
  standalone: false
})
export class SdsArchivePickerComponent implements OnChanges, OnDestroy {
  @Input() businessId!: number;
  @Input() visible = false;
  @Input() multiple = false;
  @Output() filesSelected = new EventEmitter<SdsArchiveFile[]>();
  selectedIds = new Set<number>();
  addedIds = new Set<number>();
  private filesSubscription: Subscription | null = null;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() fileSelected = new EventEmitter<SdsArchiveFile>();

  loading = false;
  error = false;
  files: SdsArchiveFile[] = [];
  searchTerm = '';

  // ----- Inline preview -----
  previewVisible = false;
  previewLoading = false;
  previewUrl: SafeResourceUrl | null = null;
  previewName = '';
  private previewBlobUrl: string | null = null;
  private previewRequestSubscription: Subscription | null = null;

  constructor(
    private readonly sdsApi: SdsApiService,
    private readonly sanitizer: DomSanitizer
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      this.searchTerm = '';
      this.selectedIds.clear();
      this.closePreview();
      this.loadFiles();
    }
  }

  ngOnDestroy(): void {
    this.filesSubscription?.unsubscribe();
    this.cancelPreviewRequest();
    this.revokePreviewBlobUrl();
  }

  get filteredFiles(): SdsArchiveFile[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      return this.files;
    }
    return this.files.filter(file => file.name.toLowerCase().includes(term));
  }

  get selectableFiles(): SdsArchiveFile[] {
    return this.filteredFiles.filter(file => !this.addedIds.has(file.id));
  }

  get allVisibleSelected(): boolean {
    const files = this.selectableFiles;
    return files.length > 0 && files.every(file => this.selectedIds.has(file.id));
  }

  get someVisibleSelected(): boolean {
    return !this.allVisibleSelected && this.selectableFiles.some(file => this.selectedIds.has(file.id));
  }

  toggleAllVisible(): void {
    const deselect = this.allVisibleSelected;
    for (const file of this.selectableFiles) {
      if (deselect) {
        this.selectedIds.delete(file.id);
      } else {
        this.selectedIds.add(file.id);
      }
    }
  }

  onVisibleChange(value: boolean): void {
    this.visible = value;
    this.visibleChange.emit(value);
    if (!value) {
      this.closePreview();
    }
  }

  toggle(file: SdsArchiveFile): void {
    if (this.addedIds.has(file.id)) return;
    if (this.selectedIds.has(file.id)) {
      this.selectedIds.delete(file.id);
    } else {
      this.selectedIds.add(file.id);
    }
  }

  confirmSelection(): void {
    const selected = this.files.filter(file => this.selectedIds.has(file.id) && !this.addedIds.has(file.id));
    if (selected.length === 0) return;
    this.filesSelected.emit(selected);
    this.onVisibleChange(false);
  }

  select(file: SdsArchiveFile): void {
    if (this.addedIds.has(file.id)) return;
    this.fileSelected.emit(file);
    this.onVisibleChange(false);
  }

  preview(file: SdsArchiveFile): void {
    this.cancelPreviewRequest();
    this.revokePreviewBlobUrl();
    this.previewName = file.name;
    this.previewUrl = null;
    this.previewLoading = true;
    this.previewVisible = true;

    this.previewRequestSubscription = this.sdsApi.getArchivePdfPreviewFile(this.businessId, file.id, file.name).subscribe({
      next: previewFile => {
        this.previewBlobUrl = URL.createObjectURL(previewFile);
        this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(`${this.previewBlobUrl}#zoom=100`);
        this.previewLoading = false;
      },
      error: () => {
        this.previewLoading = false;
        this.previewVisible = false;
        this.revokePreviewBlobUrl();
        this.previewRequestSubscription = null;
      },
      complete: () => {
        this.previewRequestSubscription = null;
      }
    });
  }

  closePreview(): void {
    this.cancelPreviewRequest();
    this.previewVisible = false;
    this.previewUrl = null;
    this.previewName = '';
    this.previewLoading = false;
    this.revokePreviewBlobUrl();
  }

  private cancelPreviewRequest(): void {
    if (this.previewRequestSubscription) {
      this.previewRequestSubscription.unsubscribe();
      this.previewRequestSubscription = null;
    }
  }

  private revokePreviewBlobUrl(): void {
    if (this.previewBlobUrl) {
      URL.revokeObjectURL(this.previewBlobUrl);
      this.previewBlobUrl = null;
    }
  }

  private loadFiles(): void {
    if (!this.businessId) {
      return;
    }

    this.loading = true;
    this.error = false;
    this.files = [];
    this.addedIds.clear();

    this.filesSubscription?.unsubscribe();
    this.filesSubscription = forkJoin([
      this.sdsApi.getArchivePdfs(this.businessId),
      this.sdsApi.getArchivePdfs(this.businessId, true)
    ]).subscribe({
      next: ([available, added]) => {
        this.addedIds = new Set(added.map(file => file.Id));
        // A file may become linked between requests; show it once, as already added.
        const docs = new Map([...available, ...added].map(file => [file.Id, file]));
        this.files = Array.from(docs.values())
          .map(d => ({ id: d.Id, name: d.Name, size: d.Size }))
          .sort((a, b) => a.name.localeCompare(b.name));
        this.loading = false;
      },
      error: () => {
        this.error = true;
        this.loading = false;
      }
    });
  }
}
